import React, { useState } from "react";
import { Container, Box, Typography, Paper, List, ListItem, ListItemText, Alert } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";
import Footer from "../../components/layout/Footer";

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const [openForm, setOpenForm] = useState(false);
  const [state, handleSubmit] = useForm("mojqqnjb");

  const handleOpenForm = () => {
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    if (state.succeeded) {
      setOpenForm(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Delete Account - App20</title>
        <meta name="description" content="Instructions for deleting your App20 account" />
      </Helmet>
      
      <Container maxWidth="md">
        <Box pt={3} pb={4}>
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 16L6 10L12 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Delete Account</h1>
            <div className="w-20" />
          </div>

          <Alert severity="warning" sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> Deleting your account is permanent. All your data, history, and associated information will be removed and cannot be recovered.
            </Typography>
          </Alert>

          <Paper elevation={0} sx={{ p: 4, backgroundColor: "#f5f5f5", mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
              Before You Delete
            </Typography>
            <Typography variant="body1" paragraph>
              Please note the following - everything will be permanently deleted:
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Profile details (name, username, etc.)"
                  secondary="Your complete profile information cannot be recovered."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Phone number and email"
                  secondary="Contact information will be permanently removed."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="UPI ID and payment data"
                  secondary="All payment methods and transaction records deleted."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Personal information"
                  secondary="All personal details will be removed."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Transaction history and activities"
                  secondary="Complete activity history will be permanently erased."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account balance and rewards"
                  secondary="Any remaining balance or rewards will be forfeited."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Login sessions and tokens"
                  secondary="All active sessions will be terminated immediately."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Preferences and settings"
                  secondary="All your preferences and custom settings will be deleted."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="All associated data"
                  secondary="Any other data linked to your account will be removed."
                />
              </ListItem>
            </List>
          </Paper>

          <Paper elevation={0} sx={{ p: 4, backgroundColor: "#f5f5f5", mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
              How to Delete Your Account ?
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Step 1: Open the App20 app"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 2: Log in using your phone number and OTP"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 3: Go to your Profile screen"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 4: Scroll to the bottom"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 5: Tap 'Delete Account'"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 6: Read the warning carefully"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 7: Confirm deletion"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 8: Wait for processing"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Step 9: You will be logged out immediately"
                />
              </ListItem>
            </List>
          </Paper>

          <Paper elevation={0} sx={{ p: 4, backgroundColor: "#fff3e0", border: "1px solid #ffb74d" }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
              Need Help?
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions or concerns about deleting your account, please reach out to our support team:
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Email"
                  secondary="contact@app20.in"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Contact Form"
                  secondary={
                    <button 
                      onClick={handleOpenForm}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm bg-none border-none cursor-pointer p-0"
                    >
                      Submit a support request
                    </button>
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Box>
      </Container>

      <Footer />

      {/* Contact Form Modal */}
      {openForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            {/* Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">Contact Support</h2>
            </div>

            {/* Success Message */}
            {state.succeeded ? (
              <div className="p-6 text-center">
                <div className="text-green-600 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-gray-900 font-medium mb-4">Thank you for reaching out!</p>
                <p className="text-gray-600 text-sm mb-6">We'll get back to you soon.</p>
                <button
                  onClick={handleCloseForm}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                {/* Form Content */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      placeholder="Your name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      required
                    />
                    <ValidationError 
                      prefix="Name" 
                      field="name"
                      errors={state.errors}
                      className="text-red-600 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      required
                    />
                    <ValidationError 
                      prefix="Email" 
                      field="email"
                      errors={state.errors}
                      className="text-red-600 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder="Your message here..."
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none"
                      required
                    />
                    <ValidationError 
                      prefix="Message" 
                      field="message"
                      errors={state.errors}
                      className="text-red-600 text-sm mt-1"
                    />
                  </div>

                  {/* Footer */}
                  <div className="border-t border-gray-200 pt-4 flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={state.submitting}
                      className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {state.submitting ? "Sending..." : "Send"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
